/**
 * GA4 e-commerce-events (relevantie Fase 3). We sturen `view_item` / `add_to_cart` / `purchase`
 * met `item_id` = onze slug, zodat GA4 klik- en conversiedata per artikel verzamelt. De
 * `aggregatePopularity`-functie haalt die later (zodra een GA4 property-ID is ingesteld) via de
 * GA4 Data API op en mengt 'm in de "Relevantie"-sortering.
 *
 * gtag loopt al via het toestemmingssysteem (Consent Mode v2). Zonder GA-ID is `window.gtag`
 * afwezig → alles is een no-op. Consent Mode bepaalt zelf of er (cookieloos) gemeten wordt.
 */
import { contentGroup } from "@/lib/content-group";
import { ensureAnalyticsLoaded, prepareTags } from "./consent";

export interface GAItem {
  item_id: string;
  item_name?: string;
  price?: number; // euro's (excl. of incl. — indicatief voor GA4)
  quantity?: number;
  item_category?: string;
}

function gaEvent(name: string, params: Record<string, unknown>): void {
  try {
    // Stub + config eerst neerzetten: het eerste event (de page_view van de landingspagina) kan
    // eerder komen dan de CookieBanner die initConsent aanroept — zie prepareTags.
    prepareTags();
    window.gtag?.("event", name, params);
  } catch {
    /* gtag niet geladen of geblokkeerd → stil negeren */
  }
}

/**
 * Google Ads-conversies vuren sinds 01-08-2026 RECHTSTREEKS via gtag (send_to-labels
 * hieronder), niet meer via een GTM-container: de GTM-laag (gtm.js + Google-tag
 * GT-552KDS2M) kostte ~370ms main-thread voor werk dat gtag zelf al doet. De labels
 * komen 1-op-1 uit de oude GTM-conversietags (conversie-ID AW-17102065516); de
 * container staat nog onaangeroerd in Tag Manager als terugvaloptie.
 * Consent Mode (ad_storage) gate't deze events net als voorheen.
 */
const ADS_ID = (import.meta.env.VITE_GOOGLE_ADS_ID as string | undefined) || "";
const ADS_LABELS = {
  purchase: "enSvCITj09ocEOye89o_",
  lead_offerte: "_VaSCJfZydMcEOye89o_",
  lead_zakelijk: "RE-mCJrZydMcEOye89o_",
  lead_montage: "xH9ACJ3ZydMcEOye89o_",
  lead_contact: "QDoRCJmusv4cEOye89o_",
} as const;

function adsConversion(label: keyof typeof ADS_LABELS, params: Record<string, unknown> = {}): void {
  if (!ADS_ID) return;
  gaEvent("conversion", { send_to: `${ADS_ID}/${ADS_LABELS[label]}`, ...params });
}

/**
 * Klantgegevens voor Enhanced Conversions (Google Ads). We sturen ze PLAT mee in de dataLayer;
 * GTM's 'Door gebruiker verstrekte gegevens'-variabele hasht ze (SHA-256) vóór verzending, en
 * Consent Mode (ad_user_data) bepaalt of het überhaupt meegaat. Zo matcht Google de conversie aan
 * een ingelogde Google-gebruiker → 10-30% méér gemeten conversies, zonder dat wij ruwe PII sturen.
 */
export interface UserData {
  email?: string;
  phone?: string;
  firstName?: string;
  lastName?: string;
}

/** NL-telefoon → E.164 (Google wil dat formaat voor de match). Best-effort. */
function normalizePhone(p?: string): string | undefined {
  const d = (p ?? "").replace(/[^\d+]/g, "");
  if (!d) return undefined;
  if (d.startsWith("+")) return d;
  if (d.startsWith("0")) return `+31${d.slice(1)}`;
  if (d.startsWith("31")) return `+${d}`;
  return d;
}

/** Bouwt het `user_data`-object voor de conversie-push (leeg → undefined, dan sturen we niets). */
function buildUserData(u?: UserData): Record<string, unknown> | undefined {
  if (!u) return undefined;
  const ud: Record<string, unknown> = {};
  const email = u.email?.trim().toLowerCase();
  if (email) ud.email = email;
  const phone = normalizePhone(u.phone);
  if (phone) ud.phone_number = phone;
  const first = u.firstName?.trim();
  const last = u.lastName?.trim();
  if (first || last) ud.address = { ...(first ? { first_name: first } : {}), ...(last ? { last_name: last } : {}) };
  return Object.keys(ud).length ? ud : undefined;
}

/**
 * Zet de klantgegevens op de (rechtstreeks geladen) Google-tag voor Enhanced Conversions.
 * De Ads-conversie vuurt via GTM, maar deelt dezelfde `window.gtag` + hetzelfde Ads-account, dus
 * data die hier met `gtag('set','user_data')` wordt gezet, koppelt Google aan de conversie-hit
 * (Enhanced Conversions-methode "Google-tag" in Ads). Vooral nodig voor de AANKOOP, waar op de
 * betaalresultaat-pagina geen e-mailveld staat om automatisch uit te lezen. Consent Mode
 * (ad_user_data) bepaalt of het daadwerkelijk meegaat. We sturen 'm ook mee in de dataLayer
 * (`user_data`), zodat een eventuele GTM-methode later óók kan werken.
 */
function setAdsUserData(user?: UserData): void {
  const ud = buildUserData(user);
  if (!ud) return;
  try {
    window.gtag?.("set", "user_data", ud);
  } catch {
    /* gtag niet geladen of geblokkeerd → stil negeren */
  }
}

/**
 * Lead-aanvraag (offerte/zakelijk/montage). Vuurt tegelijk:
 *  - GA4-standaardevent `generate_lead` (met `lead_type`), zodat de lead ook los in de
 *    GA4-rapporten verschijnt en als sleutelgebeurtenis te markeren is;
 *  - de Google Ads-conversie (direct via gtag, send_to-label per soort), mét
 *    Enhanced Conversions (`user_data`) wanneer we e-mailadres/telefoon/naam kennen.
 * Consent Mode bepaalt in beide gevallen of er (cookieloos) gemeten wordt.
 */
export function trackLead(kind: "offerte" | "zakelijk" | "montage", user?: UserData): void {
  // Forceer het laden van gtag.js nu meteen (i.p.v. te wachten op idle/interactie): een lead is
  // zeldzaam en waardevol genoeg dat we het risico niet lopen om 'm kwijt te raken doordat de
  // klant vlak na het versturen wegnavigeert. Zie ensureAnalyticsLoaded() voor de volledige uitleg.
  ensureAnalyticsLoaded();
  // Offerte en montage-via-de-winkelwagen worden door de SERVER gemeld (createOrder → GA4), want
  // die weet zeker dát de aanvraag binnen is. Hier alleen de aanvragen zónder order — anders zou
  // dezelfde lead twee keer geteld worden.
  if (kind === "zakelijk") {
    gaEvent("zakelijk_aanvraag", { currency: "EUR", value: 1 });
    gaEvent("generate_lead", { currency: "EUR", value: 1, lead_type: kind });
  }
  setAdsUserData(user); // Enhanced Conversions op de Google-tag
  adsConversion(`lead_${kind}`);
}

/**
 * Montage-aanvraag via het losse formulier op /glas-laten-plaatsen (geen winkelwagen, dus geen
 * order en geen serverzijdige meting). Zelfde eventnaam als de montage-offerte uit de winkelwagen,
 * zodat je in GA4 één cijfer ziet voor "hoeveel mensen willen het laten plaatsen".
 */
export function trackMontageForm(user?: UserData): void {
  ensureAnalyticsLoaded();
  gaEvent("montage_aanvraag", { currency: "EUR", value: 1, bron: "formulier" });
  gaEvent("generate_lead", { currency: "EUR", value: 1, lead_type: "montage" });
  setAdsUserData(user);
  adsConversion("lead_montage");
}

/**
 * Onderwerpen uit het contactformulier die een ECHTE aanvraag zijn, en niet een servicebericht
 * van iemand die al besteld heeft. Alleen deze gaan als lead naar Google Ads.
 *
 * Waarom het onderscheid. "Retour / herroeping", "Garantie / transportschade", "Bestelling" en
 * "Betaling / factuur" komen van bestaande klanten — die als conversie meetellen zou het slim
 * bieden laten sturen op klantenservice in plaats van op nieuwe klanten, en een campagne belonen
 * die vooral schademeldingen oplevert. "Glas op maat / advies", "Levering" (meestal een vraag
 * vooraf) en "Iets anders" zijn wél oriënterend.
 *
 * In GA4 gaat ELK bericht mee (contact_aanvraag met het onderwerp erbij), zodat je in de
 * rapporten de volledige instroom ziet en niet alleen het deel dat we als lead tellen.
 */
const CONTACT_LEAD_ONDERWERPEN = ["Glas op maat / advies", "Levering", "Iets anders"];

export function isContactLead(onderwerp: string): boolean {
  return CONTACT_LEAD_ONDERWERPEN.includes(onderwerp);
}

/**
 * Bericht via het contactformulier (/contact).
 *
 * Dit formulier werd tot 20-09-2026 HELEMAAL niet gemeten: geen GA4-event, geen Ads-conversie,
 * en de Cloud Function meldde ook niets. De contactpagina staat wél in de top 3 van bekeken
 * pagina's, dus een deel van de binnenkomende aanvragen was in beide systemen onzichtbaar — dat
 * verklaarde waarom Ads drie leadconversies op "Vereist aandacht" had staan terwijl er gewoon
 * aanvragen binnenkwamen.
 */
/**
 * Hoe ver iemand komt in een configurator — en waar hij vastloopt.
 *
 * Tussen `view_item` en `add_to_cart` zat een gat waar 92% van de bezoekers in verdween (okt 2026:
 * 2.101 keer bekeken, 163 keer in de winkelwagen) en waarin we niets maten. Je kon dus zien dát
 * mensen weggingen, nooit waar. Deze twee events vullen dat gat.
 *
 * `configurator_stap` is bewust één eventnaam met een `stap`-parameter, geen vier losse events:
 * in GA4 hoef je dan één aangepaste dimensie aan te melden in plaats van vier aparte rapporten
 * aan elkaar te knopen.
 *
 * `configurator_blokkade` is de waardevolste van de twee. Dat is iemand die wíl bestellen en het
 * niet kan — een maat buiten de productiegrenzen, een combinatie die op aanvraag gaat. Een te
 * hoge prijs kun je niet repareren, dit wel.
 *
 * Elke stap hoort per bezoek één keer geteld te worden; de configurator houdt dat zelf bij (wie
 * drie keer aan de breedte draait, is niet drie keer verder gekomen).
 */
export type ConfiguratorStap = "maat" | "keuze" | "bewerking";

export function trackConfiguratorStap(stap: ConfiguratorStap, soort: string): void {
  gaEvent("configurator_stap", { stap, soort: soort.slice(0, 100) });
}

export function trackConfiguratorBlokkade(reden: string, soort: string): void {
  gaEvent("configurator_blokkade", { reden: reden.slice(0, 100), soort: soort.slice(0, 100) });
}

export function trackContactForm(onderwerp: string, user?: UserData): void {
  // Net als bij een lead: niet wachten op idle, de bezoeker kan meteen wegnavigeren.
  ensureAnalyticsLoaded();
  gaEvent("contact_aanvraag", { onderwerp });
  if (!isContactLead(onderwerp)) return; // servicebericht: wel meten, niet als lead tellen
  gaEvent("generate_lead", { currency: "EUR", value: 1, lead_type: "contact" });
  setAdsUserData(user); // Enhanced Conversions op de Google-tag
  adsConversion("lead_contact");
}

/**
 * Nieuw klantaccount. GA4's standaardevent `sign_up` hoort hierbij — daarom heet de
 * nieuwsbrief-inschrijving hieronder sinds 12-08-2026 anders: die twee door elkaar heen tellen
 * gaf één hoop waarin je niet zag of iemand een account maakte of alleen een mailadres achterliet.
 */
export function trackAccountSignup(): void {
  ensureAnalyticsLoaded();
  gaEvent("sign_up", { method: "account" });
}

/**
 * Nieuwsbrief-inschrijving (footer). GA4-standaardevent `sign_up`, zodat het als sleutelgebeurtenis
 * te markeren is en meetelt in rapporten — vóór deze aanroep ging een inschrijving alleen naar
 * Firestore en was nergens in de meting terug te zien. Zeldzaam genoeg om, net als trackLead/
 * trackPurchase, niet te wachten op idle/interactie: forceer het laden van gtag.js nu meteen.
 */
export function trackNewsletterSignup(): void {
  ensureAnalyticsLoaded();
  gaEvent("nieuwsbrief_inschrijving", { method: "footer" });
}

/**
 * SPA page_view. React Router wisselt van route zonder een echte paginalaad, dus zonder deze
 * aanroep meet GA4 alleen de allereerste pagina van een bezoek — elke navigatie daarna is
 * onzichtbaar. gtag.js zelf stuurt bewust GEEN automatische page_view meer (send_page_view:false
 * in consent.ts): dit is de enige plek die dat nog doet, dus zowel de eerste als elke latere
 * pagina van een sessie loopt via hetzelfde pad.
 */
let laatstGetrackteRoute: string | null = null;
export function trackPageView(path: string, title?: string): void {
  if (path === laatstGetrackteRoute) return; // React kan een effect twee keer vuren (StrictMode)
  laatstGetrackteRoute = path;
  gaEvent("page_view", {
    page_location: typeof window !== "undefined" ? `${window.location.origin}${path}` : path,
    page_path: path,
    // Soort pagina (GA4-content grouping). Zonder dit is een padverkenning op 2.700 losse
    // adressen onleesbaar; zie content-group.ts.
    content_group: contentGroup(path),
    ...(title ? { page_title: title } : {}),
  });
}

/**
 * Stabiele GA4-naam per configurator-soort.
 *
 * De winkelwagen-titel bevat de maten ("Isolatieglas HR++ 1200 × 800"), dus daarop groeperen zou
 * van elke configuratie een eigen regel in de rapporten maken. Deze namen zijn één bron voor
 * `view_item` (in de configurator) en `add_to_cart` (in de winkelwagen); lopen ze uiteen, dan
 * sluiten "bekeken" en "in winkelwagen" niet op elkaar aan.
 */
// Eén bron voor browser én server (packages/core/src/ga4-item.ts): de serverzijdige aankoop moet
// dezelfde namen gebruiken, anders ontmoeten bekijken en kopen elkaar niet in de GA4-rapporten.
export { GA_SOORT_NAAM } from "@glastotaal/core";

/**
 * Zoekopdracht in de headerzoeker.
 *
 * GA4's automatische zoekmeting (`view_search_results`) kijkt naar `?q=` in de URL. Die komt er
 * alleen als iemand op Enter drukt en op /zoeken belandt. Wie typt en meteen een suggestie
 * aanklikt — het normale gedrag — landt rechtstreeks op het artikel en werd nooit geteld. In 28
 * dagen leverde dat één gemeten zoekopdracht op, terwijl de zoeker wel degelijk gebruikt wordt.
 *
 * Dit event vuurt na het typen (ontdubbeld), dus óók voor de suggestie-klikkers. Het aantal
 * resultaten gaat mee: een zoekopdracht met NUL resultaten is de waardevolste regel in het
 * rapport — dat is een klant die iets vraagt en geen antwoord krijgt.
 */
export function trackSearch(term: string, aantalResultaten: number): void {
  const q = term.trim();
  if (q.length < 3) return; // losse letters zijn getyp, geen zoekopdracht
  gaEvent("search", { search_term: q.slice(0, 100), aantal_resultaten: aantalResultaten });
}

export function trackViewItem(item: GAItem | null | undefined): void {
  if (!item?.item_id) return;
  gaEvent("view_item", { currency: "EUR", value: item.price ?? 0, items: [item] });
}

export function trackAddToCart(item: GAItem | null | undefined): void {
  if (!item?.item_id) return;
  gaEvent("add_to_cart", { currency: "EUR", value: (item.price ?? 0) * (item.quantity ?? 1), items: [item] });
}

/** Totale waarde van een itemlijst (prijs × aantal), voor de funnel-events. */
function sumValue(items: GAItem[]): number {
  return items.reduce((t, i) => t + (i.price ?? 0) * (i.quantity ?? 1), 0);
}

/** Winkelwagen bekeken (cart-pagina/-drawer geopend met items erin). */
export function trackViewCart(items: GAItem[]): void {
  if (items.length === 0) return;
  gaEvent("view_cart", { currency: "EUR", value: sumValue(items), items });
}

/** Item uit de winkelwagen verwijderd. */
export function trackRemoveFromCart(item: GAItem | null | undefined): void {
  if (!item?.item_id) return;
  gaEvent("remove_from_cart", { currency: "EUR", value: (item.price ?? 0) * (item.quantity ?? 1), items: [item] });
}

/** Afrekenen gestart (checkout-flow geopend). */
export function trackBeginCheckout(items: GAItem[]): void {
  if (items.length === 0) return;
  gaEvent("begin_checkout", { currency: "EUR", value: sumValue(items), items });
}

/** Verzendkeuze gemaakt (bezorgen/afhalen). `shippingTier` = "ship" | "pickup" o.i.d. */
export function trackAddShippingInfo(items: GAItem[], shippingTier?: string): void {
  if (items.length === 0) return;
  gaEvent("add_shipping_info", { currency: "EUR", value: sumValue(items), shipping_tier: shippingTier, items });
}

/** Betaalgegevens ingevuld (vlak vóór doorsturen naar Mollie). */
export function trackAddPaymentInfo(items: GAItem[]): void {
  if (items.length === 0) return;
  gaEvent("add_payment_info", { currency: "EUR", value: sumValue(items), items });
}

/** Artikellijst bekeken (categorie-/overzichtspagina). */
export function trackViewItemList(items: GAItem[], listName?: string): void {
  if (items.length === 0) return;
  gaEvent("view_item_list", { item_list_name: listName, items });
}

/** Artikel aangeklikt vanuit een lijst. */
export function trackSelectItem(item: GAItem | null | undefined, listName?: string): void {
  if (!item?.item_id) return;
  gaEvent("select_item", { item_list_name: listName, items: [item] });
}

export function trackPurchase(
  transactionId: string,
  items: GAItem[],
  value: number,
  user?: UserData,
  tax?: number,
  shipping?: number,
): void {
  if (!transactionId) return;
  // Kritiek moment: dit vuurt op de bedankpagina na een harde redirect vanaf Mollie. De hele app
  // (en dus deze module) start daar helemaal opnieuw op, en een klant die net heeft afgerekend
  // sluit die tab vaak meteen. Zonder deze aanroep wachtte gtag.js op idle/interactie — sluit de
  // klant de tab binnen die tijd, dan gaat de aankoop (én de Ads-conversie) stil verloren.
  ensureAnalyticsLoaded();
  // GA4 krijgt de aankoop NIET meer hier vandaan. Dit event vuurde alleen als de klant terugkwam
  // op de bedankpagina mét een gevulde winkelwagen; op 12-08-2026 stonden er 4 bestellingen
  // tegenover 1 gemeten aankoop. De Cloud Function `mollieWebhook` meldt de aankoop nu bij GA4
  // zodra Mollie de betaling bevestigt — dat mist niemand. Twee bronnen zou dubbel tellen.
  void tax;
  void shipping;
  void items;
  setAdsUserData(user); // Enhanced Conversions op de Google-tag (e-mail bewaard bij het afrekenen)
  // Google Ads-conversie "Aankoop" — direct via gtag met transactiewaarde en -ID,
  // zodat Ads dubbele conversies op hetzelfde ordernummer dedupliceert.
  adsConversion("purchase", { transaction_id: transactionId, value, currency: "EUR" });
}

/**
 * GA4-identiteit van deze bezoeker: `client_id` (welke browser) en `session_id` (welk bezoek).
 *
 * Nodig omdat de aankoop sinds 12-08-2026 door de SERVER wordt gemeld, op het moment dat Mollie de
 * betaling bevestigt — de bedankpagina haalt lang niet elke klant. Zonder deze twee zou GA4 die
 * aankoop als een nieuwe, directe bezoeker binnenkrijgen en zouden de kanaalrapporten juist
 * slechter worden dan ze nu zijn.
 *
 * `gtag('get', …)` beantwoordt de callback pas als gtag.js geladen is; daarom een korte tijdslimiet
 * en een lege uitkomst als er (nog) niets is. Meten mag het afrekenen nooit ophouden.
 */
export function ga4Identity(timeoutMs = 800): Promise<{ clientId?: string; sessionId?: string }> {
  const id = (import.meta.env.VITE_GA_MEASUREMENT_ID as string | undefined) || "";
  if (!id || typeof window === "undefined" || !window.gtag) return Promise.resolve({});
  return new Promise((resolve) => {
    const uit: { clientId?: string; sessionId?: string } = {};
    let klaar = 0;
    const af = (): void => {
      if (++klaar >= 2) resolve(uit);
    };
    const timer = setTimeout(() => resolve(uit), timeoutMs);
    const done = (): void => {
      if (klaar >= 2) clearTimeout(timer);
    };
    const g = window.gtag;
    if (!g) { clearTimeout(timer); resolve(uit); return; }
    try {
      g("get", id, "client_id", (v: unknown) => {
        if (typeof v === "string") uit.clientId = v;
        af();
        done();
      });
      g("get", id, "session_id", (v: unknown) => {
        if (v != null) uit.sessionId = String(v);
        af();
        done();
      });
    } catch {
      clearTimeout(timer);
      resolve(uit);
    }
  });
}
